import React, { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Navigate, Routes, Route } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";

import Home from "../features/public/pages/Home";
import { SHOW_PUBLIC_PROJECTS } from "../features/public/publicVisibility";

import ProjectDetails from "../features/public/pages/ProjectDetails";
import MainLayout from "../components/layout/MainLayout";
import ScrollToTop from "../components/common/ScrollToTop";  // <-- Add this import (adjust path if needed)
import AdminDashboard from "../features/admin/pages/AdminDashboard";
import AddNewProject from "../features/admin/pages/AddNewProject";
import EditProject from "../features/admin/pages/EditProject";
import AddProjectUpdate from "../features/admin/pages/AddProjectUpdate";
import AdminProjectDetails from "../features/admin/pages/ProjectDetails";
import ClientDashboard from "../features/client/Dashboard";
import { getSession, notifyAuthChanged } from "./auth";
import { AuthProvider, useAuth } from "./AuthSession";

const Projects = lazy(() => import("../features/public/pages/Projects"));

function RoleRoute({ allowedRoles, children }) {
  const { isAuthenticated } = useAuth();
  const session = getSession();
  if (isAuthenticated && session && allowedRoles.includes(session.role)) return children;
  return <Navigate to="/" replace />;
}

function DashboardRedirect() {
  const { isAuthenticated } = useAuth();
  const session = getSession();
  if (!isAuthenticated || !session) return <Navigate to="/" replace />;
  return <Navigate to={session.role === "CLIENT" ? "/client/dashboard" : "/admin/dashboard"} replace />;
}

function AppRoutes() {
  const { isAuthenticated } = useAuth();
  useEffect(() => { AOS.init({ duration: 800, once: false }); }, []);

  return (
    <>
      <ScrollToTop />  {/* <-- Add this here, inside BrowserRouter */}
      <Routes>
        <Route
          element={
            <MainLayout
              isAuthenticated={isAuthenticated}
              setIsAuthenticated={notifyAuthChanged}
            />
          }
        >

          <Route path="/" element={<Home />} />
          <Route path="*" element={<Home />} />
          <Route path="/home" element={<Home />} />
          <Route path="/projects" element={SHOW_PUBLIC_PROJECTS ? <Suspense fallback={<div role="status" style={{ minHeight: "60vh", padding: "48px" }}>Loading projects...</div>}><Projects /></Suspense> : <Navigate to="/" replace />} />
          <Route path="/project/:id" element={SHOW_PUBLIC_PROJECTS ? <ProjectDetails /> : <Navigate to="/" replace />} />
          <Route
            path="/login"
            element={<Navigate to="/" replace />}
          />
        </Route>

        <Route path="/admin/projects/:id/edit" element={<RoleRoute allowedRoles={["ADMIN", "SUPERADMIN"]}><EditProject /></RoleRoute>} />
        <Route path="/admin/projects/:id/updates/new" element={<RoleRoute allowedRoles={["ADMIN", "SUPERADMIN"]}><AddProjectUpdate /></RoleRoute>} />
        <Route path="/dashboard" element={<DashboardRedirect />} />
        <Route path="/admin/projects" element={<RoleRoute allowedRoles={["ADMIN", "SUPERADMIN"]}><AdminDashboard section="Projects" /></RoleRoute>} />
        <Route path="/admin/clients" element={<RoleRoute allowedRoles={["ADMIN", "SUPERADMIN"]}><AdminDashboard section="Clients" /></RoleRoute>} />
        <Route path="/admin/callbacks" element={<RoleRoute allowedRoles={["ADMIN", "SUPERADMIN"]}><AdminDashboard section="Callback Requests" /></RoleRoute>} />
        <Route path="/admin/accounts" element={<RoleRoute allowedRoles={["SUPERADMIN"]}><AdminDashboard section="Admin Accounts" /></RoleRoute>} />
        <Route
          path="/admin/dashboard"
          element={
            <RoleRoute allowedRoles={["ADMIN", "SUPERADMIN"]}>
              <AdminDashboard />
            </RoleRoute>
          }
        />
        <Route
          path="/admin/projects/:id"
          element={
            <RoleRoute allowedRoles={["ADMIN", "SUPERADMIN"]}>
              <AdminProjectDetails />
            </RoleRoute>
          }
        />
        <Route
          path="/client/dashboard"
          element={
            <RoleRoute allowedRoles={["CLIENT"]}>
              <ClientDashboard />
            </RoleRoute>
          }
        />
        {[["/client/projects", "Projects"], ["/client/projects/:id", "Project Details"], ["/client/updates", "Project Updates"], ["/client/documents", "Documents"]].map(([path, section]) => <Route key={path} path={path} element={<RoleRoute allowedRoles={["CLIENT"]}><ClientDashboard key={section} section={section} /></RoleRoute>} />)
        }
        <Route path="/adashboard" element={<Navigate to="/admin/dashboard" replace />} />
        <Route
          path="/add-project"
          element={
            <RoleRoute allowedRoles={["ADMIN", "SUPERADMIN"]}>
              <AddNewProject />
            </RoleRoute>
          }
        />


      </Routes>
    </>
  );
}

export default function App() {
  return <BrowserRouter><AuthProvider><AppRoutes /></AuthProvider></BrowserRouter>;
}
